import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerCloseButton, DrawerBody } from '@fiverr-private/overlay';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import { useBreakpoint } from '@fiverr-private/hooks';
import { Listings } from '../listings';
import { useLandingPage } from '../../context/LandingPageContext';
import { handleResponseFailure } from '../utils';
import { INTERNAL_SERVER_ERROR_CODE } from '../../consts';
import { AppI18n } from '../../../i18n';
import { BriefEditor } from '../BriefEditor';
import { MessageSentModal } from '../modals/MessageSentModal';
import { useSessionStorage, SessionDataKeys } from '../../hooks/useSessionStorage';
import { useSellersWithCTA } from '../listings/useSellersWithCTA';

export const BottomDrawer = () => {
    const {
        isBottomDrawerOpen,
        setIsBottomDrawerOpen,
        setBriefBoxInputValue,
        setGeneratedBrief,
        setSellers,
        setSellerIdsToExclude,
        error,
        setError,
        isMatchSellersLoading,
    } = useLandingPage();

    const [isMessageSentModalOpen, setIsMessageSentModalOpen] = useState(false);
    const [messageSentSellerName, setMessageSentSellerName] = useState('');

    const handleClose = () => {
        setIsBottomDrawerOpen(false);
        setBriefBoxInputValue(null);
        setGeneratedBrief(null);
        setSellers([]);
        setSellerIdsToExclude([]);
        setError(null);
    };

    useEffect(() => {
        if (error) {
            logger.error(error);
        }
    }, [error]);

    const listingsScrollRef = useRef<HTMLDivElement>(null);
    const wrapperScrollRef = useRef<HTMLDivElement>(null);
    const breakpoint = useBreakpoint();

    const { cacheStorageData, popStorageData } = useSessionStorage();

    const isSmallScreen = breakpoint === 'default' || breakpoint === 'sm';

    const { sellersWithCTA } = useSellersWithCTA({
        onMessageSent: useCallback((username: string) => {
            setMessageSentSellerName(username);
            setIsMessageSentModalOpen(true);
        }, []),
        onSignUpSuccess: useCallback(() => {
            if (isSmallScreen && wrapperScrollRef.current) {
                cacheStorageData(SessionDataKeys.SCROLL_POSITION, wrapperScrollRef.current.scrollTop.toString());
            } else if (listingsScrollRef.current) {
                cacheStorageData(SessionDataKeys.SCROLL_POSITION, listingsScrollRef.current.scrollTop.toString());
            }
        }, [cacheStorageData, isSmallScreen]),
    });

    useEffect(() => {
        if (sellersWithCTA.length) {
            const savedPosition = popStorageData(SessionDataKeys.SCROLL_POSITION);

            if (savedPosition && isSmallScreen && wrapperScrollRef.current) {
                wrapperScrollRef.current.scrollTop = +savedPosition;
            } else if (savedPosition && listingsScrollRef.current) {
                listingsScrollRef.current.scrollTop = +savedPosition;
            }
        }
    }, [sellersWithCTA, popStorageData, isSmallScreen]);

    return (
        <>
            {error && handleResponseFailure(INTERNAL_SERVER_ERROR_CODE)}
            <Drawer isOpen={isBottomDrawerOpen} onClose={handleClose}>
                <DrawerContent position="bottom" isFullScreen>
                    <DrawerHeader>
                        <AppI18n k="bottom_drawer.header" />
                        <DrawerCloseButton />
                    </DrawerHeader>
                    <DrawerBody noPadding>
                        <Stack
                            width="100%"
                            height="100%"
                            direction={{ default: 'column', md: 'row' }}
                            gap="0"
                            ref={wrapperScrollRef}
                            overflow={{ default: 'auto', md: 'visible' }}
                        >
                            <BriefEditor />
                            <Divider direction={{ default: 'horizontal', md: 'vertical' }} />
                            <Listings
                                sellersWithCTA={sellersWithCTA}
                                isMatchSellersLoading={isMatchSellersLoading}
                                ref={listingsScrollRef}
                            />
                        </Stack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
            <MessageSentModal
                sellerName={messageSentSellerName}
                isOpen={isMessageSentModalOpen}
                onClose={() => setIsMessageSentModalOpen(false)}
            />
        </>
    );
};
